import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";

import { useAuthStateListener } from "@/hooks/useAuthStateListener";
import { useDeepLinkHandler } from "@/hooks/useDeepLinkHandler";

// Each route group is its own lazy chunk — visiting /edulyt/login or
// /vipgarg-admin/* will not load storefront providers, header, footer, etc.
const StorefrontShell = React.lazy(() => import("@/shells/StorefrontShell"));
const EdulytAuthShell = React.lazy(() => import("@/shells/EdulytAuthShell"));
const TeacherShell = React.lazy(() => import("@/shells/TeacherShell"));
const StudentShell = React.lazy(() => import("@/shells/StudentShell"));
const AdminShell = React.lazy(() => import("@/shells/AdminShell"));

// Aggressive client-side caching to minimise Supabase egress.
// Most reads in this app are near-static (products, categories, homepage,
// policies, FAQs). Keeping data fresh for 5 min and skipping refetch on
// mount / window focus dedupes the vast majority of route-change refetches
// that otherwise show up as "cached egress" on the Supabase dashboard.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,      // 5 min
      gcTime: 30 * 60 * 1000,        // 30 min in memory
    },
  },
});

const RootFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function AppContent() {
  useAuthStateListener();
  useDeepLinkHandler();

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      
      <Suspense fallback={<RootFallback />}>
        <Routes>
          {/* Admin console */}
          <Route path="/vipgarg-admin/*" element={<AdminShell />} />

          {/* Edulyt portal — auth pages (lean, no storefront code) */}
          <Route path="/edulyt/*" element={<EdulytAuthShell />} />

          {/* Legacy redirects to /edulyt/* */}
          <Route path="/unified/login" element={<Navigate to="/edulyt/login" replace />} />
          <Route path="/unified/forget-password" element={<Navigate to="/edulyt/forgot-password" replace />} />
          <Route path="/unified/reset-password" element={<Navigate to="/edulyt/reset-password" replace />} />

          {/* Edulyt portal — teacher and student dashboards */}
          <Route path="/teacher/*" element={<TeacherShell />} />
          <Route path="/student/*" element={<StudentShell />} />

          {/* Storefront — everything else */}
          <Route path="/*" element={<StorefrontShell />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
